import * as React from 'react';

import { GUID, HouseholdType, OpportunityView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useDockState } from '@/views/dock/dock-state';
import { formatDate, getHouseholdName } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { useGlobalAccount } from 'views/household/household-queries';
import { useTaskMentionSuggestions } from 'views/tasks/task-queries';

import { useOpportunity } from '../opportunity-queries';
import { OpportunityActivitySection } from './OpportunityActivitySection';
import { OpportunityCommentSection } from './OpportunityCommentSection';
import { OpportunityFormType, toOpportunityFormValues } from './OpportunityForm';
import { OpportunityStatusBadge } from './OpportunityStatusBadge';

export interface OpportunityViewDetailsSheetProps {
    opportunityGuid: GUID<OpportunityView>;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onOpenDeleteDialog?: (opportunityGuid: GUID<OpportunityView>) => void;
}

export const OpportunityViewDetailsSheet = (props: OpportunityViewDetailsSheetProps) => {
    const { opportunityGuid } = props;
    const { data: opportunity, isLoading } = useOpportunity(opportunityGuid);
    const { data: household } = useGlobalAccount(opportunity?.householdGUID);
    const { addUpdateOpportunityDockItem } = useDockState();
    const { currentPersonOrgGUID } = useAuthContext();
    const mentionSuggestions = useTaskMentionSuggestions(opportunity?.householdGUID ?? undefined);
    const [commentDraft, setCommentDraft] = React.useState('');

    if (isLoading) {
        return (
            <Sheet open={props?.open} onOpenChange={props.onOpenChange}>
                <SheetContent side="right" className="w-3/4 overflow-y-auto sm:w-96" aria-describedby={undefined}>
                    <SheetHeader>
                        <SheetTitle>Loading opportunity details...</SheetTitle>
                    </SheetHeader>
                </SheetContent>
            </Sheet>
        );
    }

    if (!opportunity) {
        return (
            <Sheet open={props?.open} onOpenChange={props.onOpenChange}>
                <SheetContent side="right" className="w-3/4 overflow-y-auto sm:w-96" aria-describedby={undefined}>
                    <SheetHeader>
                        <SheetTitle>Opportunity not found</SheetTitle>
                    </SheetHeader>
                </SheetContent>
            </Sheet>
        );
    }

    const householdName = household ? getHouseholdName(household) : 'Unknown Household';

    return (
        <Sheet open={props?.open} onOpenChange={props.onOpenChange}>
            <SheetContent side="right" className="w-3/4 overflow-y-auto sm:w-96" aria-describedby={undefined}>
                <SheetHeader>
                    <SheetTitle className="text-xl font-semibold">
                        {opportunity.name || 'Untitled Opportunity'}
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                {(household?.householdType as HouseholdType) === HouseholdType.PROSPECT
                                    ? 'Prospect'
                                    : 'Household'}
                            </Label>
                            <p className="mt-1 text-sm">{householdName}</p>
                        </div>

                        <div>
                            <Label className="text-sm font-medium text-gray-500">Status</Label>
                            <div className="mt-1">
                                <OpportunityStatusBadge status={opportunity.status} />
                            </div>
                        </div>

                        {opportunity.description && (
                            <div>
                                <Label className="text-sm font-medium text-gray-500">Description</Label>
                                <p className="mt-1 text-sm">{opportunity.description}</p>
                            </div>
                        )}

                        {opportunity.value && (
                            <div>
                                <Label className="text-sm font-medium text-gray-500">Value</Label>
                                <p className="mt-1 text-sm font-medium">
                                    ${Number(opportunity.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        )}

                        <div>
                            <Label className="text-sm font-medium text-gray-500">Created</Label>
                            <p className="mt-1 text-sm">{formatDate(opportunity.createdAt)}</p>
                        </div>

                        <div>
                            <Label className="text-sm font-medium text-gray-500">Last Updated</Label>
                            <p className="mt-1 text-sm">{formatDate(opportunity.updatedAt)}</p>
                        </div>
                    </div>

                    <div className="flex gap-2 border-t pt-4">
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                                props.onOpenChange?.(false);
                                addUpdateOpportunityDockItem(
                                    opportunityGuid,
                                    opportunity.householdGUID,
                                    toOpportunityFormValues(opportunity) as OpportunityFormType,
                                );
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="flex-1"
                            onClick={() => props.onOpenDeleteDialog?.(opportunityGuid)}
                        >
                            Delete
                        </Button>
                    </div>
                    <div className="rounded-md border p-4">
                        <OpportunityCommentSection
                            opportunityGuid={opportunityGuid}
                            draft={commentDraft}
                            onDraftChange={setCommentDraft}
                            mentionSuggestions={mentionSuggestions}
                        />
                    </div>
                    <div className="rounded-md border p-4">
                        <OpportunityActivitySection
                            opportunityGuid={opportunityGuid}
                            tenantGUID={household?.tenantGUID}
                            currentPersonOrgGUID={currentPersonOrgGUID}
                            mentionSuggestions={mentionSuggestions}
                        />
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default OpportunityViewDetailsSheet;
